// Bodymap surface constants. Pivoted 2026-05-17 (post-1.63.0 scaffold)
// after Antenore pushed back on hand-drawing 15 pain regions: instead
// of a separate Inkscape-drawn overlay, the pain layer reuses the
// existing muscle slugs from react-native-body-highlighter directly.
// "Spalla destra pain" = tap on front-deltoids OR back-deltoids;
// "lombare" = lower-back; "cervicale" = neck; "bicipite" = biceps;
// etc. Joint-specific regions without a direct muscle (literal knee
// patella, ankle joint) get approximated by the surrounding muscle
// pair (quadriceps + hamstring lower, calves + tibialis).
// Architectural detail in the `project_bodymap_base` memory.
//
// Why this lives in shared:
//   - PAIN_SEVERITY_COLOURS is the source of truth for the
//     red/yellow/green tokens used by the BodyMap component (1.65.x +)
//     AND the per-exercise pain dot rendered in the Catalog row when
//     pain mode is on.
//   - MUSCLE_SLUGS is the canonical slug union the four BodyMap
//     consumers (Profile picker, Catalog dot, Logger worked overlay,
//     Progress heatmap) and the future classifier (1.67.x) all key off.
//     The npm libs ship slightly different slug sets — the platform-
//     specific BodyMap.tsx wrappers map this canonical list to each
//     lib's strings.

// Severity colour for the per-exercise pain layer. Red = avoid for an
// active pain muscle; yellow = caution (no heavy load, ramp up);
// green = safe. The classification matrix that produces these
// (exercise slug × muscle slug → severity) comes from the catalog-
// processing agent run planned for 1.67.x; the colour constants live
// here so the BodyMap component and the per-exercise dot share one
// source of truth.
// Hue selection (1.74.0): the prior palette had pain red bordering on
// the brand orange `#ff5a1f` used for worked-muscles overlays — Antenore
// 2026-05-19: "fa casino con i muscoli attivati". Shifted red to a
// purer hue with no orange tint and yellow to a brighter saturated
// yellow distinct from the salmon secondary worked muscle. Green is
// kept on the colour map for back-compat with painMap rows written
// before 1.74.0 — the picker no longer emits it (cycle drops green)
// and consumers ignore green pain via the painRisk combineCell rule
// (green pain → no risk surface), so legacy data renders invisibly
// rather than breaking the GET parse.
import { weightedVolumeKg } from "./setVolume.js";
import type { HeatmapRamp } from "./themes/types.js";

export const PAIN_SEVERITY_COLOURS = {
  red: "#dc2626",
  yellow: "#facc15",
  green: "#1aa872",
} as const;
export type PainSeverity = keyof typeof PAIN_SEVERITY_COLOURS;

// Canonical 20-slug union used across the JL bodymap surfaces. Picked
// to be the intersection-friendly superset of what
// `react-native-body-highlighter` (mobile) and `react-body-highlighter`
// (web) expose. Anatomy choices follow the rbh naming (which splits
// deltoids into front/back); the mobile wrapper collapses the two to
// rnbh's single "deltoids" slug and uses the visible view to
// disambiguate. Slugs missing on either platform (no rbh tibialis, no
// rnbh abductors) get silently dropped by the platform wrapper, which
// is fine for picker UX (the user can't tap a region that doesn't
// render anyway).
export const MUSCLE_SLUGS = [
  "chest",
  "biceps",
  "triceps",
  "forearm",
  "front-deltoids",
  "back-deltoids",
  "trapezius",
  "upper-back",
  "lower-back",
  "abs",
  "obliques",
  "quadriceps",
  "hamstring",
  "gluteal",
  "adductors",
  "abductors",
  "calves",
  "tibialis",
  "neck",
  "head",
] as const;
export type MuscleSlug = (typeof MUSCLE_SLUGS)[number];

// Short display labels for MuscleSlug used by the post-session summary's
// derived sessionLabel ("QUAD · POSTERIOR" from the top two muscles by
// volume) and by the share card's muscle bar list. Uppercase, brand-
// style, English-only by design (these are short tags in a card layout,
// not user-facing copy that benefits from translation — they pair with
// the user's exercise names which are already canonical English per
// [[feedback_exercise_names_canonical_english]]). The "head" slug has
// no useful label in a training context and falls through to the
// MUSCLE_SLUGS literal if anyone queries it.
export const MUSCLE_LABEL: Record<MuscleSlug, string> = {
  chest: "CHEST",
  biceps: "BICEPS",
  triceps: "TRICEPS",
  forearm: "FOREARMS",
  "front-deltoids": "SHOULDERS",
  "back-deltoids": "REAR DELT",
  trapezius: "TRAPS",
  "upper-back": "BACK",
  "lower-back": "LOWER BACK",
  abs: "CORE",
  obliques: "CORE",
  quadriceps: "QUAD",
  hamstring: "POSTERIOR",
  gluteal: "GLUTES",
  adductors: "ADDUCTORS",
  abductors: "ABDUCTORS",
  calves: "CALVES",
  tibialis: "TIBIALIS",
  neck: "NECK",
  head: "HEAD",
};

// Which view of the body each slug renders on. Used by the picker UX
// so taps don't fall on slugs that the current view can't display
// (front view + lower-back tap = noise). A few muscles (forearm, neck,
// head) appear on both — rbh renders them on both views, rnbh on the
// one matching `side`.
export const MUSCLE_VIEW: Record<MuscleSlug, "front" | "back" | "both"> = {
  chest: "front",
  biceps: "front",
  triceps: "back",
  forearm: "both",
  "front-deltoids": "front",
  "back-deltoids": "back",
  trapezius: "back",
  "upper-back": "back",
  "lower-back": "back",
  abs: "front",
  obliques: "front",
  quadriceps: "front",
  hamstring: "back",
  gluteal: "back",
  adductors: "front",
  abductors: "back",
  calves: "back",
  tibialis: "front",
  neck: "both",
  head: "both",
};

export type BodyView = "front" | "back";

// Figure preference passed down from the user's Profile.preferredFigure
// (see packages/shared/src/schemas/profile.ts). Decoupled from sex —
// any user can pick either figure. The mobile wrapper maps
// 'feminine'→rnbh's gender='female'; the web wrapper (rbh@2.0.5)
// ignores it because the lib only ships a male SVG. Default at the
// wrapper layer is 'masculine' when the prop is omitted.
export type BodyFigure = "masculine" | "feminine";

// Per-slug paint instruction the BodyMap component renders. Colour is
// freeform hex so both the pain palette (PAIN_SEVERITY_COLOURS) and a
// future worked-volume gradient (brand-orange ramp) can drive the same
// component. Intensity is rnbh-native (1..5 maps to a darkening blend
// inside the lib); web wrapper currently ignores it (rbh has its own
// frequency-to-colour scheme that we sidestep by passing one
// IExerciseData entry per colour bucket).
export type MusclePaint = {
  colour: string;
  intensity?: 1 | 2 | 3 | 4 | 5;
};

// Convenience: a paint map from MuscleSlug to MusclePaint. Sparse —
// only painted muscles need an entry. The picker uses this same shape
// (slug → { colour: PAIN_SEVERITY_COLOURS[severity] }) so the pain
// state and the display state share one renderer.
export type BodyMapValue = Partial<Record<MuscleSlug, MusclePaint>>;

// Helper for the common pain-picker case: turn a slug → severity map
// into a BodyMapValue the component can paint.
export function paintFromSeverity(
  severityBySlug: Partial<Record<MuscleSlug, PainSeverity>>,
): BodyMapValue {
  const out: BodyMapValue = {};
  for (const [slug, sev] of Object.entries(severityBySlug)) {
    if (!sev) continue;
    out[slug as MuscleSlug] = { colour: PAIN_SEVERITY_COLOURS[sev] };
  }
  return out;
}

// The live catalog uses a different muscle-slug vocabulary from the
// 20-slug canonical set this component renders against. Catalog slugs
// were chosen to match common training language (lats, traps, glutes,
// hamstrings) while the canonical set follows the npm libs' anatomy
// model (upper-back, trapezius, gluteal, hamstring). One catalog slug
// may map to several canonical slugs (`core` → abs + obliques + lower-
// back) and vice versa. Unknown catalog slugs return an empty array,
// which means the muscle is silently absent from the bodymap — fine
// for niche slugs we haven't classified, but worth re-checking when
// the catalog grows.
//
// Library limitation: deltoids anatomically have three heads (front /
// middle aka lateral / rear), but rnbh + rbh only ship SVG paths for
// front and back. The catalog slug `lateral-delts` therefore paints on
// `back-deltoids` — convention shared with both libs, where the side
// head reads on the back view. Re-renders cleanly when/if we ever swap
// for a library that exposes a middle-deltoid slug.
const CATALOG_TO_CANONICAL: Readonly<Record<string, readonly MuscleSlug[]>> = {
  abductors: ["abductors"],
  abs: ["abs"],
  adductors: ["adductors"],
  "anterior-delts": ["front-deltoids"],
  biceps: ["biceps"],
  calves: ["calves"],
  chest: ["chest"],
  core: ["abs", "obliques", "lower-back"],
  erectors: ["lower-back"],
  forearm: ["forearm"],
  forearms: ["forearm"],
  glutes: ["gluteal"],
  gluteal: ["gluteal"],
  hamstring: ["hamstring"],
  hamstrings: ["hamstring"],
  head: ["head"],
  "lateral-delts": ["back-deltoids"],
  lats: ["upper-back"],
  "lower-back": ["lower-back"],
  neck: ["neck"],
  obliques: ["obliques"],
  pectoralis: ["chest"],
  quadriceps: ["quadriceps"],
  "rear-delts": ["back-deltoids"],
  rhomboids: ["upper-back"],
  tibialis: ["tibialis"],
  trapezius: ["trapezius"],
  traps: ["trapezius"],
  triceps: ["triceps"],
  "upper-back": ["upper-back"],
};

export function mapCatalogToCanonical(catalogSlug: string): readonly MuscleSlug[] {
  return CATALOG_TO_CANONICAL[catalogSlug] ?? [];
}

// Brand-tinted colours for the worked-muscles overlay. Primary
// muscles paint with full brand orange so they read at a glance;
// secondary muscles get a softer tint so they sit visually under
// the primaries but are still visible on both light and dark
// themes. These could move into design-tokens but they're only
// consumed by the bodymap surface today.
export const WORKED_MUSCLE_COLOURS = {
  primary: "#ff5a1f",
  secondary: "#ffb38c",
} as const;

export type WorkedExerciseInput = {
  primary?: readonly string[];
  secondary?: readonly string[];
};

// Aggregate worked muscles across a set of exercises into a paint map.
// Primary muscles win over secondary on overlap (a row that is primary
// for `chest` and secondary for `chest` paints primary). Caller passes
// the exercises that were actually performed (e.g. one entry per
// Logger draft entry with non-empty completed sets, or per Workout
// entry over a Progress window); this function doesn't care about
// volume weighting — that's the heatmap territory and lives in a
// separate helper.
export function workedMusclesFromExercises(
  inputs: ReadonlyArray<WorkedExerciseInput>,
): BodyMapValue {
  const primary = new Set<MuscleSlug>();
  const secondary = new Set<MuscleSlug>();
  for (const inp of inputs) {
    for (const cat of inp.primary ?? []) {
      for (const c of mapCatalogToCanonical(cat)) primary.add(c);
    }
    for (const cat of inp.secondary ?? []) {
      for (const c of mapCatalogToCanonical(cat)) secondary.add(c);
    }
  }
  const out: BodyMapValue = {};
  for (const slug of secondary) {
    out[slug] = { colour: WORKED_MUSCLE_COLOURS.secondary };
  }
  for (const slug of primary) {
    out[slug] = { colour: WORKED_MUSCLE_COLOURS.primary };
  }
  return out;
}

// Five-step full-orange ramp. This is now the brand / share-card ramp and the
// DEFAULT for the heatmap functions below. In-app callers pass the neutral
// theme-aware `heatmapRamp` (design-tokens.ts) instead, so orange marks only
// the single highest-volume muscle per the colour budget. rbh on web doesn't
// support rnbh's intensity blend, so we emit a distinct hex per bucket and
// pass the matching colour to rnbh too; the rendering stays identical across
// surfaces (the colour itself is the gradient, not rnbh's intensity field).
// DS-RAMP5 — the tuple-5 type is the guard: a 6th stop here would emit
// `intensity: 6` out of the `1 | 2 | 3 | 4 | 5` contract, so the length is
// pinned at the definition rather than trusted at the `(bucketIdx + 1)` cast.
export const HEATMAP_PALETTE: HeatmapRamp = [
  "#ffd6c2",
  "#ffba9c",
  "#ff9d76",
  "#ff7c4a",
  "#ff5a1f",
] as const;

// Minimal subset of a workout row that the heatmap reads. Mirrors
// `WorkoutExerciseEntry.sets` + `Workout.entries[].exerciseId` shape
// without dragging in the full schema. Keep the prop shape narrow
// so the helper stays testable and platform-neutral.
export type HeatmapSetInput = {
  type: string;
  reps?: number;
  weight?: number;
  loadedSides?: 1 | 2;
  fail?: boolean;
};

export type HeatmapEntryInput = {
  exerciseId: string;
  sets: ReadonlyArray<HeatmapSetInput>;
};

export type HeatmapWorkoutInput = {
  startedAt: string;
  entries: ReadonlyArray<HeatmapEntryInput>;
};

export type HeatmapExerciseLookup = (
  exerciseId: string,
) => { primary?: readonly string[]; secondary?: readonly string[] } | undefined;

// Build a worked-muscle heatmap from a window of saved workouts. The
// caller filters/passes the time window. For each weighted set in the
// window — failed sets included, see the AP-0p note at the volume loop
// below — we compute `weight × reps × sides` and
// distribute that volume to the entry's catalog row: primary muscles
// take the full volume, secondary muscles take half (rough proxy for
// the share they actually contribute). Each canonical muscle's total
// volume is then mapped to a 1..5 intensity bucket relative to the
// highest-volume muscle in the window — the highest-loaded muscle
// always reads as the darkest shade, lighter muscles fall in
// proportion. Non-weighted set kinds (bodyweight, cardio, carry, p1rm
// programming variants) currently contribute zero volume; refine
// later if we need them in the heatmap signal.
//
// Returns an empty map when no muscle accumulated any volume — the
// caller should render the component conditionally rather than show
// an empty body.
export function volumeHeatmapFromWorkouts(
  workouts: ReadonlyArray<HeatmapWorkoutInput>,
  lookup: HeatmapExerciseLookup,
  sinceMs: number,
  ramp: readonly string[] = HEATMAP_PALETTE,
): BodyMapValue {
  const volumeBySlug: Partial<Record<MuscleSlug, number>> = {};
  for (const w of workouts) {
    const t = Date.parse(w.startedAt);
    if (!Number.isFinite(t) || t < sinceMs) continue;
    for (const entry of w.entries) {
      const ex = lookup(entry.exerciseId);
      if (!ex) continue;
      let entryVolume = 0;
      for (const s of entry.sets) {
        if (s.type !== "weighted") continue;
        // A fail counts like any set (AP-0p model): reps = clean done, fail
        // is a pure label — no exclusion, so the heatmap agrees with
        // Progress/SessionResult volume.
        const reps = s.reps ?? 0;
        const weight = s.weight ?? 0;
        if (reps <= 0 || weight <= 0) continue;
        entryVolume += weightedVolumeKg(weight, reps, s.loadedSides);
      }
      if (entryVolume <= 0) continue;
      for (const cat of ex.primary ?? []) {
        for (const c of mapCatalogToCanonical(cat)) {
          volumeBySlug[c] = (volumeBySlug[c] ?? 0) + entryVolume;
        }
      }
      for (const cat of ex.secondary ?? []) {
        for (const c of mapCatalogToCanonical(cat)) {
          volumeBySlug[c] = (volumeBySlug[c] ?? 0) + entryVolume * 0.5;
        }
      }
    }
  }
  return volumeBySlugToHeatmap(volumeBySlug, ramp);
}

// Map a pre-aggregated per-slug volume map to a BodyMapValue using the
// same 5-step orange ramp. Extracted from volumeHeatmapFromWorkouts so
// the post-session summary widget + share card can paint the bodymap
// from SessionResult.volumeByMuscle without recomputing from raw sets.
//
// Same normalisation rule: the highest-volume slug in the input always
// reads as the darkest shade (#ff5a1f), every other slug falls into a
// bucket proportional to its share of that max. Empty input or all-zero
// volumes return {} so the caller can render the component conditionally
// rather than show an empty body.
//
// Mobile-vs-web wrapper divergence: mobile's react-native-body-highlighter
// reads MusclePaint.intensity (1..5) natively; web's react-body-highlighter
// only reads colour. We populate both so the same value object renders
// consistently on both surfaces.
export function volumeBySlugToHeatmap(
  volumeBySlug: Partial<Record<MuscleSlug, number>>,
  ramp: readonly string[] = HEATMAP_PALETTE,
): BodyMapValue {
  let maxV = 0;
  for (const v of Object.values(volumeBySlug)) {
    if (v != null && v > maxV) maxV = v;
  }
  if (maxV === 0) return {};
  const out: BodyMapValue = {};
  for (const [slug, v] of Object.entries(volumeBySlug)) {
    if (v == null || v <= 0) continue;
    const ratio = v / maxV;
    const bucketIdx = Math.min(
      ramp.length - 1,
      Math.max(0, Math.ceil(ratio * ramp.length) - 1),
    );
    const colour = ramp[bucketIdx] ?? ramp[0];
    // bucketIdx is 0-based across the ramp; intensity is 1-based 1..5 per
    // rnbh's contract. The +1 shift keeps the two aligned with the same
    // darkest-bucket = highest-volume invariant.
    // ⚠️ DS-RAMP5 — this cast is a RUNTIME contract, not a checked one: `ramp`
    // is `readonly string[]` because the helper deliberately buckets against
    // whatever ramp it is handed (a 2-stop ramp yields 1..2 — pinned in
    // bodymap.test.ts). The cast is sound only because every PRODUCTION ramp is
    // a `HeatmapRamp` 5-tuple — HEATMAP_PALETTE and every theme's, both guarded
    // at their definition. A caller that hand-builds a 6-stop ramp would still
    // emit `intensity: 6`; the tuple guard stops that at the ramp source.
    const intensity = (bucketIdx + 1) as 1 | 2 | 3 | 4 | 5;
    out[slug as MuscleSlug] = { colour: colour as string, intensity };
  }
  return out;
}
